import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Plus,
  Search,
  ArrowUpDown,
  BarChart2,
  MapPin,
  Mail,
  Phone,
  Loader2,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useClinic } from '@/hooks/useClinicSettings';
import { useSubClinics, useActivateMultiClinic } from '@/hooks/useMultiClinic';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { CreateSubClinicModal } from '@/components/modals/CreateSubClinicModal';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/cn';
import type { Json } from '@/types/database.generated';

type SortField = 'name' | 'created_at' | 'city';

interface AddressShape {
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
}

function readAddress(value: Json): AddressShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      street: typeof v.street === 'string' ? v.street : '',
      city: typeof v.city === 'string' ? v.city : '',
      zip: typeof v.zip === 'string' ? v.zip : '',
      country: typeof v.country === 'string' ? v.country : 'Danmark',
    };
  }
  return {};
}

function readActivatedModules(value: Json): Record<string, boolean> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, boolean>;
  }
  return {};
}

export function ClinicsClinics() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId, isMainClinicAdmin } = useActiveClinic();
  const { data: clinic, isLoading: clinicLoading } = useClinic(activeClinicId);
  const { data: subClinics = [], isLoading: subClinicsLoading } = useSubClinics(activeClinicId);
  const activateModule = useActivateMultiClinic(activeClinicId);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showStats, setShowStats] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Check if multi-clinic is activated
  const activatedModules = readActivatedModules(clinic?.activated_modules ?? null);
  const hasMultiClinicAccess = Boolean(activatedModules.multiClinic) || subClinics.length > 0;

  // Redirect if not main clinic admin
  if (!isMainClinicAdmin && !clinicLoading) {
    return (
      <ErrorAlert message={t('settings.clinics.unauthorized')} />
    );
  }

  const handleActivateMultiClinic = async () => {
    try {
      await activateModule.mutateAsync();
      toast.success(t('settings.clinics.multi_clinic_activated'));
    } catch (error) {
      console.error('Failed to activate multi-clinic:', error);
      toast.error(t('settings.clinics.activation_error'));
    }
  };

  // Filter and sort clinics
  const filteredAndSortedClinics = useMemo(() => {
    let filtered = [...subClinics];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((clinic) => {
        const address = readAddress(clinic.address);
        return (
          clinic.name.toLowerCase().includes(term) ||
          clinic.email.toLowerCase().includes(term) ||
          address.city?.toLowerCase().includes(term)
        );
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'city': {
          const aAddr = readAddress(a.address);
          const bAddr = readAddress(b.address);
          comparison = (aAddr.city ?? '').localeCompare(bAddr.city ?? '');
          break;
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [subClinics, searchTerm, sortField, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalClinics = subClinics.length;
    const activeClinics = subClinics.filter((c) => c.is_active).length;
    const cities = new Set(
      subClinics.map((c) => readAddress(c.address).city).filter(Boolean)
    );

    return {
      totalClinics,
      activeClinics,
      citiesCount: cities.size,
    };
  }, [subClinics]);

  if (clinicLoading || subClinicsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Activation CTA */}
      {!hasMultiClinicAccess && (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-indigo-50 p-3 dark:bg-indigo-900/20">
              <Building2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('settings.clinics.activate_title')}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('settings.clinics.activate_description')}
              </p>
              <Button
                onClick={handleActivateMultiClinic}
                disabled={activateModule.isPending}
                className="mt-4"
              >
                {activateModule.isPending
                  ? t('settings.clinics.activating')
                  : t('settings.clinics.activate_button')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Header with actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('settings.clinics.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('settings.clinics.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasMultiClinicAccess && (
            <Button
              variant="secondary"
              onClick={() => setShowStats(!showStats)}
              className="flex items-center gap-2"
            >
              <BarChart2 className="h-4 w-4" />
              {showStats ? t('settings.clinics.hide_stats') : t('settings.clinics.show_stats')}
            </Button>
          )}
          {hasMultiClinicAccess && (
            <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {t('settings.clinics.create_clinic')}
            </Button>
          )}
        </div>
      </div>

      {/* Statistics cards */}
      {showStats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('settings.clinics.stats.total_clinics')}
            </h4>
            <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
              {stats.totalClinics}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('settings.clinics.stats.active_clinics')}
            </h4>
            <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
              {stats.activeClinics}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('settings.clinics.stats.cities')}
            </h4>
            <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
              {stats.citiesCount}
            </p>
          </div>
        </div>
      )}

      {/* Search and sort */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder={t('settings.clinics.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block h-[44px] w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value as SortField)}
            className="block h-[44px] rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="name">{t('settings.clinics.sort.name')}</option>
            <option value="created_at">{t('settings.clinics.sort.created_at')}</option>
            <option value="city">{t('settings.clinics.sort.city')}</option>
          </select>
          <button
            onClick={() => setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))}
            className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            aria-label={t('settings.clinics.toggle_sort_order')}
          >
            <ArrowUpDown className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Clinics list */}
      <section className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white shadow-sm dark:divide-gray-700 dark:border-gray-700 dark:bg-card-dark">
        {filteredAndSortedClinics.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm
                ? t('settings.clinics.no_results')
                : t('settings.clinics.no_clinics')}
            </p>
          </div>
        ) : (
          filteredAndSortedClinics.map((clinic) => {
            const address = readAddress(clinic.address);
            return (
              <div
                key={clinic.id}
                className={cn(
                  'p-4 transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                  !clinic.is_active && 'opacity-60'
                )}
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-xl bg-indigo-50 p-3 dark:bg-indigo-900/20">
                    <Building2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {clinic.name}
                      </h3>
                      {!clinic.is_active && (
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          {t('settings.clinics.inactive')}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-y-2">
                      {address.street && (
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <MapPin className="mr-2 h-4 w-4" />
                          {address.street}, {address.zip} {address.city}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Mail className="mr-2 h-4 w-4" />
                        {clinic.email}
                      </div>
                      {clinic.phone && (
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Phone className="mr-2 h-4 w-4" />
                          {clinic.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/settings/clinics/${clinic.id}`)}
                    >
                      {t('settings.clinics.manage')}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* Create sub-clinic modal */}
      <CreateSubClinicModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        parentClinicType={clinic?.type}
        onSuccess={(clinicId) => {
          setShowCreateModal(false);
          toast.success(t('settings.clinics.created_successfully'));
          navigate(`/settings/clinics/${clinicId}`);
        }}
      />
    </div>
  );
}
